import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { invoicesRepo } from '../repo/invoicesRepo'
import { logAudit } from '@/lib/audit'

export function useInvoices() {
  return useQuery({
    queryKey: ['invoices'],
    queryFn: () => invoicesRepo.findAll(),
  })
}

export function useClientInvoices(clientId: string | undefined) {
  return useQuery({
    queryKey: ['invoices', 'client', clientId],
    queryFn: async () => {
      if (!clientId) return []
      try {
        return await invoicesRepo.findByClient(clientId)
      } catch (error) {
        console.error('Error loading client invoices:', error)
        throw error // Re-throw so React Query can handle it
      }
    },
    enabled: !!clientId,
  })
}

export function useCustomerInvoices(customerId: string | undefined) {
  return useQuery({
    queryKey: ['invoices', 'customer', customerId],
    queryFn: () => customerId ? invoicesRepo.findByCustomer(customerId) : [],
    enabled: !!customerId,
  })
}

export function useCreateInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invoice: any) => invoicesRepo.create(invoice),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      logAudit({
        action: 'create_invoice',
        resource_type: 'invoice',
        resource_id: data.id,
        client_id: data.client_id,
      })
    },
    onError: (error: any) => {
      console.error('Error creating invoice:', error)
      alert(`Failed to create invoice: ${error.message || 'Unknown error'}`)
    },
  })
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      invoicesRepo.update(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      logAudit({
        action: 'update_invoice',
        resource_type: 'invoice',
        resource_id: data.id,
        client_id: data.client_id,
      })
    },
  })
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => invoicesRepo.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}
